import { resolveDateLocale, useI18n } from '../../../i18n';
import { OrderStatusBadge } from './OrderStatusBadge';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export interface OrdersTableRow {
  id: string;
  code: string;
  storeName: string;
  channelLabel: string;
  status: string;
  statusLabel: string;
  paymentStatusLabel: string;
  itemsCount: number;
  totalAmount: number;
  createdAt: string;
}

interface OrdersTableProps {
  orders: OrdersTableRow[];
  isLoading?: boolean;
  onRowClick?: (order: OrdersTableRow) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onView?: (id: string) => void;
}

// ============================================
// Icons
// ============================================

function EyeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

// ============================================
// Table Header
// ============================================

function TableHeader() {
  const { t } = useI18n();
  return (
    <thead>
      <tr className="bg-[#F3F3FE]">
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('orderCode')}
        </th>
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('destination')}
        </th>
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('product')}
        </th>
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('quantity')}
        </th>
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('status')}
        </th>
        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('created')}
        </th>
        <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('action')}
        </th>
        <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-[#434655]">
          {t('view')}
        </th>
      </tr>
    </thead>
  );
}

// ============================================
// Table Row
// ============================================

interface TableRowProps {
  order: OrdersTableRow;
  onRowClick?: (order: OrdersTableRow) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onView?: (id: string) => void;
}

function TableRow({ order, onRowClick, onApprove, onReject, onView }: TableRowProps) {
  const { t } = useI18n();
  const totalQuantity = order.itemsCount;

  const productName = t('itemsCount', { count: order.itemsCount });
  const productSku = order.channelLabel || 'Unknown Channel';

  // Format date
  const { language } = useI18n();
  const createdDate = new Date(order.createdAt);
  const dateStr = createdDate.toLocaleDateString(resolveDateLocale(language, 'en-US'), { month: 'short', day: 'numeric' });
  const yearStr = createdDate.getFullYear().toString();

  // Next action in order lifecycle (PENDING không tồn tại — chờ thanh toán là PENDING_PAYMENT)
  const canApprove = order.status === 'DRAFT' || order.status === 'PENDING_PAYMENT';
  const canReject = order.status === 'DRAFT' || order.status === 'PENDING_PAYMENT';

  // Map status -> màu badge + nhãn i18n, phủ đủ 11 status của BE.
  const statusBadgeClass =
    order.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
    order.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
    order.status === 'CANCELLED' || order.status === 'VOIDED' ? 'bg-red-100 text-red-700' :
    order.status === 'REFUNDED' || order.status === 'PARTIALLY_REFUNDED' ? 'bg-orange-100 text-orange-700' :
    order.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' :
    order.status === 'PREPARING' ? 'bg-indigo-100 text-indigo-700' :
    order.status === 'READY' ? 'bg-cyan-100 text-cyan-700' :
    order.status === 'PENDING_PAYMENT' ? 'bg-amber-100 text-amber-700' :
    'bg-slate-100 text-slate-700';

  const statusLabel =
    order.status === 'DRAFT' ? t('draft') :
    order.status === 'PENDING_PAYMENT' ? t('pendingPayment') :
    order.status === 'CONFIRMED' ? t('confirmed') :
    order.status === 'PREPARING' ? t('preparing') :
    order.status === 'READY' ? t('ready') :
    order.status === 'PAID' ? t('paid') :
    order.status === 'COMPLETED' ? t('completed') :
    order.status === 'CANCELLED' ? t('cancelled') :
    order.status === 'VOIDED' ? t('voided') :
    order.status === 'REFUNDED' ? t('refunded') :
    order.status === 'PARTIALLY_REFUNDED' ? t('partiallyRefunded') :
    order.status;

  const approveLabel = 'Confirm';

  return (
    <tr 
      className="group border-t border-[#E7E7F3] hover:bg-[#FAFAFE] cursor-pointer transition-colors"
      onClick={() => onRowClick?.(order)}
    >
      {/* Order Code */}
      <td className="px-4 py-4">
        <div className="flex flex-col">
            <span 
              className="text-sm font-bold text-primary-600 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(order.id);
              }}
            >
            #{order.code}
          </span>
          <span className="text-[10px] font-medium text-[#94A3B8] uppercase">
            {order.paymentStatusLabel}
          </span>
        </div>
      </td>

      {/* Destination */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center text-primary-600">
            <BuildingIcon />
          </div>
          <span className="whitespace-nowrap text-sm font-semibold text-[#191B23]">
            {order.storeName}
          </span>
        </div>
      </td>

      {/* Product */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-7 rounded bg-[#F1F5F9] flex items-center justify-center text-slate-400">
            <CubeIcon />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-[#191B23]">
              {productName}
            </span>
            <span className="text-xs text-slate-400">
              {productSku}
            </span>
          </div>
        </div>
      </td>

      {/* Quantity */}
      <td className="px-4 py-4">
        <div className="flex flex-col">
          <span className="text-sm font-bold text-[#191B23]">
            {totalQuantity}
          </span>
          <span className="text-[10px] text-slate-400 uppercase font-medium">
            {t('units')}
          </span>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-4">
        <div className="flex flex-col items-start gap-1">
          <span className={cn(
            'text-[10px] font-extrabold uppercase px-2 py-0.5 rounded',
            statusBadgeClass
          )}>
            {statusLabel}
          </span>
          <span className="text-[10px] text-slate-400 font-medium capitalize">
            {order.statusLabel || statusLabel}
          </span>
        </div>
      </td>

      {/* Created */}
      <td className="px-4 py-4 text-sm text-[#434655] font-semibold">
        <div className="flex flex-col">
          <span>{dateStr}</span>
          <span className="text-[10px] text-slate-400 font-normal">{yearStr}</span>
        </div>
      </td>

      {/* ACTION Column (Buttons) */}
      <td className="px-4 py-4 text-center">
        <div className="flex items-center justify-center min-h-[28px]">
          {canApprove && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove?.(order.id);
                }}
                className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors"
                title={approveLabel}
              >
                <CheckIcon />
              </button>
              {canReject && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject?.(order.id);
                  }}
                  className="w-7 h-7 rounded-full bg-[#E1E2ED] text-[#434655] flex items-center justify-center hover:bg-slate-300 transition-colors"
                  title="Cancel"
                >
                  <XIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </td>

      {/* VIEW Column (Eye icon) */}
      <td className="px-4 py-4 text-right">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView?.(order.id);
          }}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          title="View Details"
        >
          <EyeIcon />
        </button>
      </td>
    </tr>
  );
}

// ============================================
// Loading Skeleton
// ============================================

function TableSkeleton() {
  return (
    <tbody>
      {[1, 2, 3, 4, 5].map((i) => (
        <tr key={i} className="border-t border-[#E7E7F3] animate-pulse">
          <td className="px-4 py-4">
            <div className="h-4 w-24 bg-slate-200 rounded" />
            <div className="h-3 w-16 bg-slate-100 rounded mt-1" />
          </td>
          <td className="px-4 py-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-100" />
              <div className="h-4 w-28 bg-slate-200 rounded" />
            </div>
          </td>
          <td className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-7 rounded bg-slate-100" />
              <div>
                <div className="h-4 w-32 bg-slate-200 rounded" />
                <div className="h-3 w-20 bg-slate-100 rounded mt-1" />
              </div>
            </div>
          </td>
          <td className="px-4 py-4">
            <div className="h-5 w-16 bg-slate-200 rounded" />
          </td>
          <td className="px-4 py-4">
            <div className="flex justify-center">
              <div className="w-3 h-3 rounded-full bg-slate-200" />
            </div>
          </td>
          <td className="px-4 py-4">
            <div className="h-6 w-20 bg-slate-200 rounded-full" />
          </td>
          <td className="px-4 py-4">
            <div className="h-4 w-16 bg-slate-200 rounded" />
          </td>
          <td className="px-4 py-4">
            <div className="h-6 w-16 bg-slate-100 rounded mx-auto" />
          </td>
          <td className="px-4 py-4">
            <div className="h-5 w-5 bg-slate-100 rounded ml-auto" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState() {
  const { t } = useI18n();
  return (
    <tbody>
      <tr>
        <td colSpan={8} className="px-4 py-16 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
              <CubeIcon />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">{t('noOrdersFound')}</h3>
            <p className="text-sm text-slate-500 max-w-sm">
              {t('thereAreNoOrdersMatchingYourCurrent')}
            </p>
          </div>
        </td>
      </tr>
    </tbody>
  );
}

// ============================================
// Main Component
// ============================================

export function OrdersTable({
  orders,
  isLoading = false,
  onRowClick,
  onApprove,
  onReject,
  onView,
}: OrdersTableProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[900px]">
        <TableHeader />
        {isLoading ? (
          <TableSkeleton />
        ) : orders.length === 0 ? (
          <EmptyState />
        ) : (
          <tbody>
            {orders.map((order) => (
              <TableRow
                key={order.id}
                order={order}
                onRowClick={onRowClick}
                onApprove={onApprove}
                onReject={onReject}
                onView={onView}
              />
            ))}
          </tbody>
        )}
      </table>
      </div>
    </div>
  );
}
